import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { printQuote } from "@/lib/printQuote";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  FileText, Plus, Pencil, Trash2, Send, Link2, Trash, Check, X, Phone, Printer,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const STATUS = {
  bozza: { label: "Bozza", cls: "bg-slate-100 text-slate-600" },
  inviato: { label: "Inviato", cls: "bg-blue-100 text-blue-700" },
  accettato: { label: "Accettato", cls: "bg-emerald-100 text-emerald-700" },
  rifiutato: { label: "Rifiutato", cls: "bg-red-100 text-red-700" },
  contatto_richiesto: { label: "Contatto richiesto", cls: "bg-amber-100 text-amber-700" },
};

const emptyRow = () => ({ descrizione: "", service_id: "", quantita: 1, prezzo_unitario: 0, sconto_perc: 0 });
const DEFAULT_CONDIZIONI = `L'attività periodica di anamnesi viene concordata con il personal trainer PT-MED di riferimento, in funzione del monitoraggio e del raggiungimento degli obiettivi a breve e lungo termine.

Abbonamento con prenotazione anticipata dell'ora valida fino ai 30 giorni successivi dal giorno di avviamento degli allenamenti.
Ogni appuntamento verrà gestito assegnando il professionista idoneo al percorso intrapreso che strutturerà e monitorerà un programma personalizzato, assicurando un'assistenza mirata e continuativa per l'allenamento. E' possibile rimandare gli appuntamenti occasionalmente in diversi giorni e orari disponibili nello stesso mese circa, solo ed esclusivamente se si avvisa il riferimento attuale PT-MED Alberto Piovan al 342 1439782 tramite messaggio o chiamata, almeno 24 ore prima.`;
const emptyForm = () => ({
  client_id: "", righe: [emptyRow()], sconto_globale_perc: 0, note: "", condizioni: DEFAULT_CONDIZIONI,
  validita: "", modalita_pagamento: "", metodo_pagamento: "",
});

export default function Preventivi() {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [modes, setModes] = useState([]);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/quotes"); setQuotes(data); }
    catch { toast.error("Errore caricamento preventivi"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/payment-modes").then(({ data }) => setModes(data));
    api.get("/payment-methods").then(({ data }) => setMethods(data));
  }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      client_id: q.client_id,
      righe: q.righe?.length ? q.righe.map((r) => ({ ...emptyRow(), ...r, service_id: r.service_id || "" })) : [emptyRow()],
      sconto_globale_perc: q.sconto_globale_perc || 0, note: q.note || "",
      condizioni: q.condizioni ?? DEFAULT_CONDIZIONI,
      validita: q.validita ? q.validita.slice(0, 10) : "", modalita_pagamento: q.modalita_pagamento || "",
      metodo_pagamento: q.metodo_pagamento || "",
    });
    setOpen(true);
  };

  const updateRow = (i, key, val) => setForm((f) => {
    const righe = [...f.righe]; righe[i] = { ...righe[i], [key]: val }; return { ...f, righe };
  });
  const onRowService = (i, sid) => {
    const svc = services.find((s) => s.id === sid);
    setForm((f) => {
      const righe = [...f.righe];
      righe[i] = { ...righe[i], service_id: sid,
        descrizione: svc ? svc.nome : righe[i].descrizione,
        prezzo_unitario: svc ? svc.prezzo : righe[i].prezzo_unitario };
      return { ...f, righe };
    });
  };
  const addRow = () => setForm((f) => ({ ...f, righe: [...f.righe, emptyRow()] }));
  const removeRow = (i) => setForm((f) => ({ ...f, righe: f.righe.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    let subtotale = 0;
    form.righe.forEach((r) => {
      let line = (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0);
      line -= line * ((parseFloat(r.sconto_perc) || 0) / 100);
      subtotale += line;
    });
    const scontoVal = subtotale * ((parseFloat(form.sconto_globale_perc) || 0) / 100);
    return { subtotale, scontoVal, totale: subtotale - scontoVal };
  }, [form]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Seleziona un paziente"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sconto_globale_perc: parseFloat(form.sconto_globale_perc) || 0,
        validita: form.validita || null,
        righe: form.righe.map((r) => ({
          descrizione: r.descrizione, service_id: r.service_id || null,
          quantita: parseFloat(r.quantita) || 0, prezzo_unitario: parseFloat(r.prezzo_unitario) || 0,
          sconto_perc: parseFloat(r.sconto_perc) || 0,
        })),
      };
      if (editing) { await api.put(`/quotes/${editing.id}`, payload); toast.success("Preventivo aggiornato"); }
      else { await api.post("/quotes", payload); toast.success("Preventivo creato"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/quotes/${id}`); toast.success("Preventivo eliminato"); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const publicLink = (q) => `${window.location.origin}/p/preventivo/${q.public_token}`;

  const sendQuote = async (q) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/send`);
      await navigator.clipboard?.writeText(data.link).catch(() => {});
      toast.success(data.email_inviata
        ? "Preventivo inviato via email · link copiato"
        : "Preventivo pronto · link copiato negli appunti");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copyLink = async (q) => {
    try { await navigator.clipboard.writeText(publicLink(q)); toast.success("Link copiato"); }
    catch { toast.error("Impossibile copiare il link"); }
  };

  const handlePrint = (q) => {
    if (!printQuote(q)) toast.error("Consenti i popup del browser per stampare il preventivo");
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Preventivi" subtitle={`${quotes.length} preventivi`}>
        <Button onClick={openNew} data-testid="btn-nuovo-preventivo" className="font-semibold">
          <Plus className="mr-1.5 h-4 w-4" /> Nuovo Preventivo
        </Button>
      </PageHeader>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8">Caricamento...</div>
      ) : quotes.length === 0 ? (
        <EmptyState icon={FileText} title="Nessun preventivo"
          description="Crea il tuo primo preventivo e invialo al paziente con un link per accettarlo, rifiutarlo o richiedere di essere contattato."
          action={<Button onClick={openNew}><Plus className="mr-1.5 h-4 w-4" /> Nuovo Preventivo</Button>} />
      ) : (
        <Card className="overflow-hidden border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Numero</TableHead>
                <TableHead>Paziente</TableHead>
                <TableHead className="hidden md:table-cell">Data</TableHead>
                <TableHead className="text-right">Totale</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`} className="hover:bg-slate-50/60">
                  <TableCell className="font-mono-data text-sm font-medium">{q.numero}</TableCell>
                  <TableCell className="font-medium text-slate-800">{q.cliente_nome}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                    {q.created_at ? format(new Date(q.created_at), "d MMM yyyy", { locale: it }) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono-data font-semibold">€ {q.totale?.toLocaleString("it-IT")}</TableCell>
                  <TableCell>
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS[q.stato]?.cls}`}>
                      {STATUS[q.stato]?.label}
                    </span>
                    {q.risposta?.telefono && <div className="text-[11px] text-amber-600 mt-0.5">Tel: {q.risposta.telefono}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" title="Invia / genera link" onClick={() => sendQuote(q)} data-testid={`send-quote-${q.id}`} className="text-primary">
                        <Send className="h-4 w-4" />
                      </Button>
                      {q.stato !== "bozza" && (
                        <Button variant="ghost" size="icon" title="Copia link" onClick={() => copyLink(q)} data-testid={`copy-quote-${q.id}`}>
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" title="Stampa / Salva PDF" onClick={() => handlePrint(q)} data-testid={`print-quote-${q.id}`}>
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Modifica" onClick={() => openEdit(q)} data-testid={`edit-quote-${q.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" data-testid={`delete-quote-${q.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare {q.numero}?</AlertDialogTitle>
                            <AlertDialogDescription>L'operazione non può essere annullata.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(q.id)} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Modifica ${editing.numero}` : "Nuovo Preventivo"}</DialogTitle>
            <DialogDescription>Aggiungi le righe di servizio, applica eventuali sconti e salva.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-5">
            <div>
              <Label>Paziente *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="quote-client-select"><SelectValue placeholder="Seleziona paziente" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.cognome} {c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Righe del preventivo</Label>
              <div className="rounded-lg border overflow-hidden">
                <div className="hidden sm:grid grid-cols-12 gap-2 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <div className="col-span-5">Descrizione / Servizio</div>
                  <div className="col-span-2 text-center">Qtà</div>
                  <div className="col-span-2 text-right">Prezzo €</div>
                  <div className="col-span-2 text-center">Sconto %</div>
                  <div className="col-span-1"></div>
                </div>
                {form.righe.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-t items-center" data-testid={`quote-row-input-${i}`}>
                    <div className="col-span-12 sm:col-span-5 space-y-1">
                      <Select value={r.service_id} onValueChange={(v) => onRowService(i, v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`quote-row-service-${i}`}><SelectValue placeholder="Servizio (opzionale)" /></SelectTrigger>
                        <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={r.descrizione} onChange={(e) => updateRow(i, "descrizione", e.target.value)} placeholder="Descrizione" data-testid={`quote-row-desc-${i}`} className="h-8 text-sm" />
                    </div>
                    <div className="col-span-4 sm:col-span-2"><Input type="number" step="1" min="0" value={r.quantita} onChange={(e) => updateRow(i, "quantita", e.target.value)} data-testid={`quote-row-qty-${i}`} className="h-8 text-sm text-center" /></div>
                    <div className="col-span-4 sm:col-span-2"><Input type="number" step="0.01" value={r.prezzo_unitario} onChange={(e) => updateRow(i, "prezzo_unitario", e.target.value)} data-testid={`quote-row-price-${i}`} className="h-8 text-sm text-right" /></div>
                    <div className="col-span-3 sm:col-span-2"><Input type="number" step="1" min="0" max="100" value={r.sconto_perc} onChange={(e) => updateRow(i, "sconto_perc", e.target.value)} className="h-8 text-sm text-center" /></div>
                    <div className="col-span-1 flex justify-end">
                      {form.righe.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeRow(i)} data-testid={`quote-remove-row-${i}`}>
                          <Trash className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="quote-add-row"><Plus className="mr-1.5 h-4 w-4" /> Aggiungi riga</Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Modalità pagamento</Label>
                <Select value={form.modalita_pagamento} onValueChange={(v) => setForm((f) => ({ ...f, modalita_pagamento: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="quote-modalita-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Metodo pagamento</Label>
                <Select value={form.metodo_pagamento} onValueChange={(v) => setForm((f) => ({ ...f, metodo_pagamento: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="quote-metodo-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{methods.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Validità</Label><Input type="date" value={form.validita} onChange={(e) => setForm((f) => ({ ...f, validita: e.target.value }))} className="mt-1.5" /></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={2} className="mt-1.5" placeholder="Condizioni, indicazioni..." /></div>
              <div className="rounded-lg border bg-slate-50 p-4 space-y-1.5">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotale</span><span className="font-mono-data">€ {totals.subtotale.toFixed(2)}</span></div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Sconto globale %</span>
                  <Input type="number" step="1" min="0" max="100" value={form.sconto_globale_perc} onChange={(e) => setForm((f) => ({ ...f, sconto_globale_perc: e.target.value }))} data-testid="quote-sconto-globale" className="h-8 w-20 text-right" />
                </div>
                <div className="flex justify-between text-sm text-red-600"><span>Sconto</span><span className="font-mono-data">− € {totals.scontoVal.toFixed(2)}</span></div>
                <div className="flex justify-between border-t pt-1.5 text-base font-bold"><span>Totale</span><span className="font-mono-data text-primary" data-testid="quote-totale">€ {totals.totale.toFixed(2)}</span></div>
              </div>
            </div>

            <div>
              <Label>Condizioni</Label>
              <Textarea value={form.condizioni} onChange={(e) => setForm((f) => ({ ...f, condizioni: e.target.value }))}
                        rows={8} data-testid="quote-condizioni" className="mt-1.5 text-sm" placeholder="Condizioni contrattuali del preventivo..." />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving} data-testid="save-quote-button">{saving ? "Salvataggio..." : "Salva"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
